import { useState, useContext } from "react";
import { PostApiContext } from "../../context/apiCalls/postApiCalls";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";

export default function AccountDetails({customer}) {
    const postApiContext = useContext(PostApiContext);
    const navigate = useNavigate();

    const [passwordStrength, setPasswordStrength] = useState(0);
    const [passwordMsg, setPasswordMsg] = useState("");
    const [submitError, setSubmitError] = useState("");
    const [showPwd, setShowPwd] = useState({
      old: false,
      new: false,
      new2: false
    });

    const checkPasswordStrength = (pwd) => {
      let score = 0;
      let msg = [];
      if (pwd.length >= 8) score++;
      else msg.push("8 caractères");
      if (/[a-z]/.test(pwd)) score++;
      else msg.push("1 minuscule");
      if (/[A-Z]/.test(pwd)) score++;
      else msg.push("1 majuscule");
      if (/[0-9]/.test(pwd)) score++;
      else msg.push("1 chiffre");
      setPasswordStrength(score);
      setPasswordMsg(msg.length === 0 ? "Mot de passe fort" : "Manque: " + msg.join(", "));
    };

      const handleChangePassword = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const data = {
          login: customer.login,
          old_password: formData.get("oldPassword"),
          new_password: formData.get("newPassword"),
          conf_new_password: formData.get("newPassword2"),
        };

        // Validation des données
        if (passwordStrength < 4) {
          setSubmitError("Le mot de passe ne respecte pas les critères de sécurité.");
          return;
        }
        if (data.newPassword !== data.newPassword2) {
          setSubmitError("Les deux nouveaux mots de passe ne sont pas identiques.");
          return;
        }
        setSubmitError("");
        
        try {
            const response = await postApiContext.postAccount(data);
            if (response.status !== "success") {
                toast.error("Erreur lors du changement de mot de passe.");
                return;
            }
            toast.success("Mot de passe modifié avec succès.");
            // Réinitialiser le formulaire
            e.target.reset();
            setPasswordStrength(0);
            setPasswordMsg("");
            setSubmitError("");
        } catch (error) {
            toast.error("Erreur lors du changement de mot de passe.");
        }
      };
    
      const handleForgotPassword = (e) => {
        e.preventDefault();
        navigate("/forgotPassword");
      };

    return(
        <div className="accountInfoGroup settingsBlock">
          <h2>Informations du compte</h2>
          <form
            className="accountInfoForm"
            onSubmit={handleChangePassword}
            autoComplete="off"
          >
            <div className="formRow">
              <label htmlFor="user-identifier">Identifiant</label>
              <input
                id="user-identifier"
                type="text"
                value={customer.login || ""}
                readOnly
                autoComplete="username"
              />
            </div>
            <div className="formRow" style={{position: 'relative'}}>
              <label htmlFor="old-password">Ancien mot de passe</label>
              <input
                id="old-password"
                type={showPwd.old ? "text" : "password"}
                name="oldPassword"
                required
                autoComplete="current-password"
              />
              <span
                className="showPwdIcon"
                style={{position: 'absolute', right: 10, top: '50%', cursor: 'pointer'}}
                onClick={() => setShowPwd(p => ({...p, old: !p.old}))}
                tabIndex={0}
                aria-label={showPwd.old ? "Masquer le mot de passe" : "Afficher le mot de passe"}
              >
                <FontAwesomeIcon icon={showPwd.old ? faEyeSlash : faEye} />
              </span>
            </div>
            <div className="formRow" style={{position: 'relative'}}>
              <label htmlFor="new-password">Nouveau mot de passe</label>
              <input
                id="new-password"
                type={showPwd.new ? "text" : "password"}
                name="newPassword"
                required
                autoComplete="new-password"
                onChange={e => checkPasswordStrength(e.target.value)}
              />
              <span
                className="showPwdIcon"
                style={{position: 'absolute', right: 10, top: '50%', cursor: 'pointer'}}
                onClick={() => setShowPwd(p => ({...p, new: !p.new}))}
                tabIndex={0}
                aria-label={showPwd.new ? "Masquer le mot de passe" : "Afficher le mot de passe"}
              >
                <FontAwesomeIcon icon={showPwd.new ? faEyeSlash : faEye} />
              </span>
            </div>
            <div className="formRow" style={{position: 'relative'}}>
              <label htmlFor="new-password2">Confirmation nouveau mot de passe</label>
              <input
                id="new-password2"
                type={showPwd.new2 ? "text" : "password"}
                name="newPassword2"
                required
                autoComplete="new-password"
              />
              <span
                className="showPwdIcon"
                style={{position: 'absolute', right: 10, top: '50%', cursor: 'pointer'}}
                onClick={() => setShowPwd(p => ({...p, new2: !p.new2}))}
                tabIndex={0}
                aria-label={showPwd.new2 ? "Masquer le mot de passe" : "Afficher le mot de passe"}
              >
                <FontAwesomeIcon icon={showPwd.new2 ? faEyeSlash : faEye} />
              </span>
            </div>
            {/* Barre de complexité */}
            <div className="formRow" style={{marginBottom: '0.5rem'}}>
              <div className="passwordStrengthBar" style={{
                height: '7px',
                borderRadius: '4px',
                background: passwordStrength === 0 ? '#eee' : passwordStrength === 4 ? '#1ec773' : passwordStrength === 3 ? '#f7b500' : '#e74c3c',
                width: passwordStrength * 25 + '%',
                transition: 'width 0.3s, background 0.3s',
                marginBottom: '2px'
              }}></div>
              <span style={{fontSize: '0.92em', color: passwordStrength === 4 ? '#1ec773' : '#e74c3c'}}>{passwordMsg}</span>
            </div>
            {submitError && (
              <div className="formRow" style={{marginBottom: '0.5rem'}}>
                <span style={{color: '#e74c3c', fontSize: '0.95em'}}>{submitError}</span>
              </div>
            )}
            <div
              className="formRow"
              style={{ marginTop: "-10px", marginBottom: "10px" }}
            >
              <p
                className="forgotPasswordLink"
                onClick={handleForgotPassword}
                style={{ fontSize: "0.95em" }}
              >
                Mot de passe oublié ?
              </p>
            </div>
            <button className="settingsSave" type="submit">
              Changer le mot de passe
            </button>
          </form>
        </div>
    )
}